import * as React from "react";
import loadsh, { capitalize } from "lodash";
import {
  Box,
  Tabs,
  Tab,
  Chip,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { footprintTabsStyles } from "../../../../_metronic/layout";
import { FootprintModel } from "../../models/Footprint";
import FootprintDetails from "./FootprintDetails";
import FootprintEmissions from "./FootprintEmissions";
import {
  AnimalType,
  FootPrintType,
  StageType,
  ReportingType,
} from "../../../../graphql/types";
import ResultAndErrors from "./ResultAndErrors";
import { FootprintStageData } from "./useFootprint";
import { FootprintCategoryName } from "../../models/Footprint/FootprintTypes";
import {
  isDairy,
  isMarineFish,
  isPig,
  isPigFootprint,
  isPoultryFootprint,
  isShrimp,
} from "../../helpers/animals";
import lookupValues from "../../helpers/lookupValues";
import featureFlags from "../../utils/featureFlags";

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

interface FootprintTabsProps {
  footprintStages: FootprintStageData[] | undefined;
  footprint: FootprintModel;
  customerID: string;
  type: string;
  baselineFarmID: string;
  comparisonReference?: string;
  setCategoryFilters: (categories: FootprintCategoryName[]) => void;
  setAnalysisGroupFilters: (analysisGroups: string[]) => void;
  setStageFilter: (stageDate: FootprintStageData) => void;
  initialTabOpened?: string | null;
  animalType?: string | undefined;
  singleStageName?: string;
  isFlag?: boolean;
  reportingType?: ReportingType;
  comparisonSelectedStage?: string;
  currentComparisonStage?: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`footprint-tabpanel-${index}`}
      aria-labelledby={`footprint-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const FootprintTabs = (props: FootprintTabsProps) => {
  const {
    footprintStages,
    footprint,
    setCategoryFilters,
    setAnalysisGroupFilters,
    setStageFilter,
    customerID,
    type,
    baselineFarmID,
    comparisonReference,
    initialTabOpened = "",
    animalType,
    singleStageName,
    isFlag = false,
    reportingType,
    comparisonSelectedStage,
    currentComparisonStage,
  } = props;
  const classes = footprintTabsStyles();
  const intl = useIntl();

  const errorTabIndex = footprint.type !== FootPrintType.CompoundFeed ? 2 : 1;
  const [value, setValue] = React.useState(
    initialTabOpened === "error" ? errorTabIndex : 0,
  );

  // needed to change value for processing meat types select
  const processingCategoryStage = React.useRef<
    FootprintStageData | undefined
  >();

  const mountDefaultValue = (stage: FootprintStageData) =>
    `${stage.stageId}${stage.animalType}${stage?.processingOutputName || ""}`;

  const getDefaultValue = () => {
    // If there is a default stage set return it, else use the older logic
    const defaultStage = footprintStages?.find((stage) => stage.isDefaultStage);
    if (defaultStage) {
      return mountDefaultValue(defaultStage);
    }

    if (animalType === AnimalType.Pig) {
      // if processing stages exist then default is the one with highest outputWeight and category fresh meat (category set earlier)
      // priority cutting
      const processingStages = footprintStages?.filter(
        (stage) => stage.stageType === StageType.Processing,
      );

      const multipleFacilitiesLength = 2;
      const singleFacilityLength = 1;

      if (processingStages?.length === multipleFacilitiesLength) {
        const defaultProcessingStage = loadsh
          .chain(footprintStages)
          .filter({
            stageType: StageType.Processing,
            originStageType: StageType.Processing,
          })
          .maxBy("outputWeight")
          .value();

        if (defaultProcessingStage) {
          return mountDefaultValue(defaultProcessingStage);
        }
      }

      if (processingStages?.length === singleFacilityLength) {
        const originStageType = isPoultryFootprint(
          processingStages[0].animalType,
        )
          ? StageType.Growing
          : StageType.Fattening;

        const defaultProcessingStage = loadsh
          .chain(footprintStages)
          .filter({
            stageType: StageType.Processing,
            originStageType,
          })
          .maxBy("outputWeight")
          .value();

        if (defaultProcessingStage) {
          return mountDefaultValue(defaultProcessingStage);
        }
      }

      let defaultStage = footprintStages?.find(
        (stage) => stage.stageType === StageType.Fattening,
      );
      if (defaultStage) {
        return mountDefaultValue(defaultStage);
      }

      defaultStage = loadsh
        .chain(footprintStages)
        .filter({ stageType: StageType.Breeding, animalType: "Piglet" })
        .maxBy("outputWeight")
        .value();

      if (defaultStage) {
        return mountDefaultValue(defaultStage);
      }
      return null;
    } else {
      const defaultStage = footprintStages?.[0];
      if (defaultStage)
        return `${defaultStage.stageId}${defaultStage.animalType}`;
    }
    return null;
  };

  const filterInitialStage = (
    stage: FootprintStageData,
    selectedStageId: string,
    selectedStageOutput?: string,
    selectedStageProcessingOutput?: string,
  ) => {
    const isActiveStage = stage.stageId === selectedStageId;
    if (selectedStageOutput && selectedStageProcessingOutput)
      return (
        isActiveStage &&
        stage.animalType === selectedStageOutput &&
        stage.processingOutputName === selectedStageProcessingOutput
      );
    if (selectedStageOutput)
      return isActiveStage && stage.animalType === selectedStageOutput;
    return isActiveStage;
  };

  const getInitialStage = () => {
    if (comparisonSelectedStage) {
      const selectedStageValues = comparisonSelectedStage.split("|");
      const selectedStage = footprintStages?.find((stage) =>
        filterInitialStage(
          stage,
          selectedStageValues[0],
          selectedStageValues[1],
          selectedStageValues[2],
        ),
      );
      if (selectedStage) return mountDefaultValue(selectedStage);
    }
    return getDefaultValue();
  };

  const [stageValue, setStageValue] = React.useState(getInitialStage());

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getOutputStageValue = (stage: FootprintStageData | undefined) => {
    if (!stage) return undefined;
    return stage.originStageId
      ? `${stage.stageId}${stage?.originStageId || ""}${
          stage.facilityCategory || ""
        }${stage?.processingOutputName || ""}`
      : undefined;
  };

  const getShrimpProcessingDefaultOutput = (
    processingOutputs: FootprintStageData[] | undefined,
  ) => {
    if (processingOutputs) {
      const findMaxOutputWeight = (
        a: FootprintStageData,
        b: FootprintStageData,
      ) =>
        a.outputWeight * (a.outputPrice ?? 1) >
        b.outputWeight * (b.outputPrice ?? 1)
          ? a
          : b;

      if (processingOutputs.length === 1) {
        return processingOutputs[0];
      }

      if (processingOutputs.length) {
        return processingOutputs.reduce(findMaxOutputWeight);
      }
    }
    return undefined;
  };

  const getProcessingDefaultOutput = (
    outputs: FootprintStageData[] | undefined,
  ) => {
    if (isShrimp(animalType as AnimalType)) {
      const output = outputs?.find((output) => output.isDefaultStage);
      if (output) return output;
      return getShrimpProcessingDefaultOutput(outputs);
    }

    return outputs?.at(0);
  };

  const getSelectedStage = (
    selectedStageId: string,
    selectedStageOutput?: string,
    selectedStageProcessingOutput?: string,
  ) => {
    const isActiveStage = stageValue?.includes(selectedStageId);
    if (selectedStageOutput && selectedStageProcessingOutput)
      return (
        isActiveStage &&
        stageValue?.includes(selectedStageOutput) &&
        stageValue?.includes(selectedStageProcessingOutput)
      );
    if (selectedStageOutput)
      return isActiveStage && stageValue?.includes(selectedStageOutput);
    return isActiveStage;
  };

  // indicator that initial footprint is selected
  const [initialOption, setInitialOption] = React.useState(false);
  React.useEffect(() => {
    if (!initialOption) {
      if (
        !footprintStages?.some((f) => f?.stageType === StageType.Processing) &&
        stageValue
      ) {
        setInitialOption(true);
        const selectedStage = footprintStages?.find(
          (f) =>
            stageValue.includes(f.stageId) && stageValue.includes(f.animalType),
        );
        if (selectedStage) {
          setStageFilter(selectedStage);
        }
      } else if (footprintStages && stageValue) {
        const selectedStage = footprintStages?.find((f) =>
          stageValue.includes(f.stageId),
        );
        if (isShrimp(animalType as AnimalType)) {
          const processingOutput = getProcessingDefaultOutput(
            selectedStage?.processingStageData,
          );
          if (selectedStage?.facilityId && processingOutput) {
            processingOutput.animalType = selectedStage.animalType;
            // if processing, set first meat category as selected
            setStageFilter(processingOutput);
          }
        } else if (
          selectedStage?.facilityId &&
          selectedStage?.processingStageData?.at(0)
        ) {
          selectedStage.processingStageData[0].animalType =
            selectedStage.animalType;
          // if processing, set first meat category as selected
          setStageFilter(selectedStage.processingStageData[0]);
        }
        setInitialOption(true);
      }
    }
    let selectedStage = footprintStages?.find((f) =>
      getSelectedStage(f.stageId),
    );

    if (!selectedStage) {
      setStageValue(getInitialStage());
      return;
    }

    if (featureFlags.ffBugsQ3s1) {
      selectedStage = footprintStages?.find((f) =>
        getSelectedStage(f.stageId, f.animalType, f.processingOutputName),
      );
      if (selectedStage) {
        setStageFilter(selectedStage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageValue, footprintStages, initialOption]);

  const getStageViewName = (stage: FootprintStageData) => {
    if (
      stage.stageType === StageType.Fattening &&
      filteredOutputFootprints.length > 0
    ) {
      return `${intl.formatMessage({
        id: "SUSTELL.ENUMS.STAGE.TYPE.FATTENING",
      })} ${intl.formatMessage({ id: "GENERAL.AND" })} ${intl.formatMessage({
        id: "SUSTEL.ENUMS.STAGE.TYPE.PROCESSING",
      })}`;
    }
    return stage.stageName;
  };

  const getStageItemName = (stage: FootprintStageData) => {
    if (
      isPoultryFootprint(stage.animalType) ||
      (isShrimp(animalType as AnimalType) &&
        stage.stageType !== StageType.Processing) ||
      isPigFootprint(stage.animalType)
    ) {
      let displayName = "";
      if (stage?.farmName) {
        displayName = stage?.farmName;
      }
      if (stage.originStageName) {
        displayName = `${displayName} - ${stage.originStageName}`;
      }
      if (stage.stageName) {
        displayName = `${displayName} - ${stage.stageName}`;
      }
      if (
        stage.animalType &&
        isPig(animalType as AnimalType) &&
        stage.stageType === StageType.Processing
      ) {
        if (stage.originStageType === StageType.Fattening) {
          displayName = `${displayName} - ` + `Pig`;
        } else {
          displayName = `${displayName} - ` + `Sow`;
        }
      } else {
        displayName = `${displayName} - ${stage.animalType}`;
      }

      return displayName;
    }

    if (isShrimp(animalType as AnimalType) && stage.stageName) {
      return `${stage.farmName} - ${stage.stageName} - Shrimp`;
    }

    if (isDairy(animalType as AnimalType)) {
      return `${stage.stageName} - ${capitalize(
        stage.animalType?.toLowerCase(),
      )}`;
    }
    // this is added after merge
    return getStageViewName(stage);
  };

  const getStageName = () => {
    const val = stageValue?.substring(0, 36);
    if (!val) {
      return isMarineFish(animalType as AnimalType)
        ? singleStageName
        : undefined;
    }
    const stage = footprintStages?.find((f) => f.stageId === val);
    if (stage) {
      return getStageItemName(stage);
    }
    return undefined;
  };

  const [stageName, setStageName] = React.useState(getStageName());

  const stageChangeHandler = (
    event:
      | React.ChangeEvent<{ name?: string | undefined; value: unknown }>
      | { target: { value: unknown } },
    isProcessing = false,
  ) => {
    // reset old value
    processingCategoryStage.current = undefined;

    // console.log("EVENT TARGET VALUE", event.target.value);
    const selectedStage = !isProcessing
      ? footprintStages?.find(
          (stage) =>
            `${stage.stageId}${stage.animalType}${
              stage?.processingOutputName || ""
            }` === event.target.value && stage.animalType,
        )
      : footprintStages
          ?.flatMap((f) => f.processingStageData)
          ?.find((stage) => getOutputStageValue(stage) === event.target.value);

    if (selectedStage) {
      if (!isProcessing) {
        setStageValue(
          `${selectedStage.stageId}${selectedStage.animalType}${
            selectedStage?.processingOutputName || ""
          }`,
        );
        // When processing is selcted from left, set fresh meat for output footprint
        if (
          selectedStage.stageType === StageType.Processing &&
          selectedStage.processingStageData?.at(0)
        ) {
          if (selectedStage?.processingStageData?.[0]) {
            setStageFilter(selectedStage.processingStageData[0]);
          }
        } else {
          setStageFilter(selectedStage);
        }
      } else {
        processingCategoryStage.current = selectedStage;
        setStageFilter(selectedStage);
      }
      setStageName(getStageItemName(selectedStage));
    }
  };

  const getNameForOutputCategory = (
    outputName: string,
    stage: FootprintStageData,
    isShrimpFootprint: boolean,
  ) => {
    const entry = Object.entries(
      lookupValues.processingOutputTypeMappings,
    ).find(([key]) => key === outputName)?.[1];

    if (
      entry === "CAT3_HIDES_AND_SKINS" &&
      isPoultryFootprint(stage.animalType)
    )
      return "CAT3_FEATHERS_AND_SKINS";

    if (isShrimpFootprint) {
      switch (entry) {
        case "FRESH_MEAT_AND_EDIBLE_OFFAL":
          return "EDIBLE";
        case "CAT3_HIDES_AND_SKINS":
          return "CATEGORY_THREE";
        case "CAT3_SLAUGHTER_BY_PRODUCTS":
          return "CATEGORY_THREE";
        case "CAT1_AND2_MATERIAL_AND_WASTE":
          return "CATEGORY_ONE_AND_A_HALF";
        default:
          return "EDIBLE";
      }
    }

    return entry || "";
  };

  const getProcessingOutputsData = () => {
    const selectedStage = footprintStages?.find(
      (stage) =>
        `${stage.stageId}${stage.animalType}${
          stage?.processingOutputName || ""
        }` === stageValue && stage.animalType,
    );
    if (
      selectedStage?.processingStageData &&
      selectedStage?.processingStageData.length > 0
    ) {
      selectedStage.processingStageData.map(
        (pd) => (pd.animalType = selectedStage.animalType),
      );
      return selectedStage.processingStageData;
    }
    return undefined;
  };

  const getCommonProcessingDropdownItem = (stage: FootprintStageData) =>
    intl.formatMessage({
      id: `SUSTELL.STAGES.PROCESSING.PACKAGING.OUTPUT.TYPE.${getNameForOutputCategory(
        stage.facilityCategory || "",
        stage,
        false,
      )}`,
    });

  const getShrimpProcessOutputName = (outputName: string | undefined) => {
    let name = outputName;

    if (outputName) {
      const nameParts = outputName.split(", id");
      if (nameParts && nameParts.length > 0 && nameParts[0])
        name = nameParts[0];
    }

    return name;
  };

  const getShrimpProcessingDropdownItem = (stage: FootprintStageData) =>
    `${getShrimpProcessOutputName(stage?.processingOutputName) || ""} ` +
    `(${intl.formatMessage({
      id: `BASELINE.FORM.SHRIMP.PROCESSING.PACKAGING.${getNameForOutputCategory(
        stage.facilityCategory || "",
        stage,
        true,
      )}`,
    })})`;

  // If baseline stage is changed, filter processing stage footprints
  const getFilteredOutputFootprints = () =>
    (getProcessingOutputsData() || []).filter(
      (item) =>
        item.originStageId &&
        item.originStageId === (stageValue || "").substring(0, 36),
    );

  const filteredOutputFootprints = getFilteredOutputFootprints() || [];

  const proccesingOutputs = getProcessingOutputsData();

  const getStageType = () => {
    const val = stageValue?.substring(0, 36);
    if (!val) {
      return undefined;
    }
    const stage = footprintStages?.find((f) => f.stageId === val);
    if (stage) {
      return stage.stageType as StageType;
    }
    return undefined;
  };

  return (
    <Box>
      {footprintStages && footprintStages.length > 0 && (
        <Box style={{ width: "100%", display: "flex", flexDirection: "row" }}>
          <Box style={{ width: "40%" }}>
            <InputLabel>
              <b>{`${intl.formatMessage({
                id: "SUSTELL.FOOTPRINT.STAGES.DROPDOWN",
              })}`}</b>
            </InputLabel>
            <Select
              name="stageName"
              defaultValue={getInitialStage}
              value={stageValue}
              variant="outlined"
              fullWidth
              onChange={(e) => stageChangeHandler(e)}
            >
              {footprintStages.map((stage: FootprintStageData) => (
                <MenuItem
                  key={`${stage.stageName}${stage.animalType}${
                    stage.processingOutputName || ""
                  }`}
                  value={`${stage.stageId}${stage.animalType}${
                    stage.processingOutputName || ""
                  }`}
                >
                  {getStageItemName(stage)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {proccesingOutputs?.length && (
            <Box style={{ marginLeft: "30px", width: "40%" }}>
              <InputLabel>
                <b>{`${intl.formatMessage({
                  id: "SUSTELL.FOOTPRINT.STAGES.DROPDOWN_SLAUGHTERHOUSE",
                })}`}</b>
              </InputLabel>
              <Select
                name="outputStageName"
                value={getOutputStageValue(
                  processingCategoryStage.current ||
                    getProcessingDefaultOutput(proccesingOutputs),
                )}
                variant="outlined"
                fullWidth
                onChange={(e) => stageChangeHandler(e, true)}
              >
                {(proccesingOutputs || []).map((stage: FootprintStageData) => (
                  <MenuItem
                    key={`${stage.stageName}${stage?.originStageId || ""}${
                      stage.facilityCategory || ""
                    }${stage?.processingOutputName || ""}`}
                    value={`${stage.stageId}${stage?.originStageId || ""}${
                      stage.facilityCategory || ""
                    }${stage?.processingOutputName || ""}`}
                  >
                    {isShrimp(animalType as AnimalType)
                      ? getShrimpProcessingDropdownItem(stage)
                      : getCommonProcessingDropdownItem(stage)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      )}
      <Box style={{ width: "100%" }}>
        <Box className={classes.tabsBox}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="footprint tabs"
            className={classes.tabs}
          >
            <Tab
              label={intl.formatMessage({
                id: "SUSTELL.FOOTPRINT.TABS.FOOTPRINT.TITLE",
              })}
              value={0}
            />
            {footprint.type !== FootPrintType.CompoundFeed && !isFlag && (
              <Tab
                label={intl.formatMessage({
                  id: "SUSTELL.FOOTPRINT.TABS.EMISSIONS.TITLE",
                })}
                value={1}
              />
            )}
            <Tab
              label={
                <Box>
                  {intl.formatMessage({
                    id: "SUSTELL.FOOTPRINT.TABS.RESULTANDQUALITYERRORS.TITLE",
                  })}
                  {footprint.countWarningsErrors > 0 && (
                    <Chip
                      variant="outlined"
                      style={{
                        color: "#E51F22",
                        borderColor: "#FCE9E9",
                        background: "#FCE9E9",
                        marginLeft: "3px",
                      }}
                      label={footprint.countWarningsErrors}
                    />
                  )}
                </Box>
              }
              value={errorTabIndex}
            />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <FootprintDetails
            footprint={footprint}
            customerID={customerID}
            type={type}
            setCategoryFilters={setCategoryFilters}
            setAnalysisGroupFilters={setAnalysisGroupFilters}
            baselineFarmID={baselineFarmID}
            comparisonReference={comparisonReference}
            stageName={stageName ?? getStageName()}
            stageType={getStageType()}
            isFlag={isFlag}
            reportingType={reportingType}
            currentComparisonStage={currentComparisonStage}
          />
        </TabPanel>
        {footprint.type !== FootPrintType.CompoundFeed && (
          <TabPanel value={value} index={1}>
            <FootprintEmissions
              footprint={footprint}
              customerID={customerID}
              type={type}
              comparisonReference={comparisonReference}
              isMarineFish={isMarineFish(animalType as AnimalType)}
              currentComparisonStage={currentComparisonStage}
            />
          </TabPanel>
        )}
        <TabPanel value={value} index={errorTabIndex}>
          <ResultAndErrors footprint={footprint} />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default FootprintTabs;
